import React from "react";

/**
 * Generic React error boundary.
 *
 * Wraps a subtree and catches any unhandled render/lifecycle exception
 * thrown by a descendant component. Without this, an unhandled exception
 * anywhere in the tree unmounts the whole app and leaves a blank/black
 * screen with nothing in the DOM and nothing actionable in a screenshot.
 *
 * With this in place, the same failure instead renders a real on-screen
 * message plus (in dev, or when ?debug=1 is present) the actual error
 * and component stack, so the next occurrence is self-diagnosing without
 * needing a live screen-share or DevTools walkthrough.
 *
 * Usage: wrap the top of the app (e.g. in index.jsx / main.jsx):
 *
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * It can also be used around any individually risky subtree (e.g. a
 * specific module route) if you want one module's crash to not take
 * down the whole shell — just wrap that route's element instead of/
 * in addition to the root.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the full component stack for on-screen display and for
    // whatever console/error-reporting hook gets wired in later.
    this.setState({ errorInfo });

    // eslint-disable-next-line no-console
    console.error("COMS unhandled error caught by ErrorBoundary:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const showDetails =
        process.env.NODE_ENV !== "production" ||
        (typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("debug") === "1");

      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px",
            fontFamily: "system-ui, -apple-system, sans-serif",
            background: "#fafafa",
            color: "#1a1a1a",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 640 }}>
            <h1 style={{ fontSize: "20px", marginBottom: "8px" }}>
              Something went wrong.
            </h1>
            <p style={{ fontSize: "14px", color: "#555", marginBottom: "20px" }}>
              COMS hit an unexpected error and couldn't continue rendering
              this screen. Reloading usually clears it. If it keeps
              happening, copy the details below (or open DevTools →
              Console) and send them over.
            </p>

            <button
              onClick={this.handleReload}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                background: "#1a1a1a",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                marginBottom: "20px",
              }}
            >
              Reload page
            </button>

            {showDetails && this.state.error && (
              <pre
                style={{
                  textAlign: "left",
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  padding: "16px",
                  fontSize: "12px",
                  overflow: "auto",
                  maxHeight: "300px",
                  color: "#b00020",
                }}
              >
                {this.state.error.toString()}
                {this.state.errorInfo &&
                  "\n\nComponent stack:\n" +
                    this.state.errorInfo.componentStack}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
